<template>
  <v-card>
    <v-card-title>
      <div class="title-and-tooltip">
        <span class="text-cinza-d10">Funil de cupons</span>
      </div>
    </v-card-title>
    <v-card-text class="funnel-container">
      <div class="arrows-and-bar-container">
        <div class="arrows">
          <div class="arrow-line" style="height: 49px; margin-top: 10px">
            <div class="arrow"></div>
          </div>
          <div class="arrow-line">
            <div class="arrow"></div>
          </div>
          <div v-if="this.offerType === 'nextbuycashback' || this.offerType === 'dynamic_rule' || this.offerType === 'crosssell'" class="arrow-line">
            <div class="arrow"></div>
          </div>
        </div>
        <div class="bars">
          <progress-funnel-bar
            color="#969ea7"
            :value="couponsWithProduct"
            is-industry
            description="Total de cupons com o produto"
            :tooltip="
              this.offerType === 'crosssell'
                ? 'Total de cupons do período da oferta que tem ao menos um dos SKU gatilhos da oferta.'
                : 'Total de cupons do período da oferta que tem ao menos um dos SKU participantes da oferta.'
            "
            hide-bar
          />

          <progress-funnel-bar
            color="#94B300"
            :value="couponsEligible"
            is-industry
            :percentage="couponsEligiblePercent"
            :barWidth="couponsEligiblePercent > 0 ? 100 : 0"
            description="Cupons com quantidade elegível"
            :tooltip="
              this.offerType === 'crosssell'
                ? 'Cupons que atingiram a quantidade mínima de produtos gatilhos da oferta.'
                : 'Cupons que atingiram a quantidade mínima de produtos participantes da oferta.'
            "
          />

          <progress-funnel-bar
            v-if="this.offerType !== 'crosssell'"
            color="#00A186"
            :value="couponsActivated"
            is-industry
            :percentage="couponsActivatedPercent"
            :bar-width="couponsActivatedPercent"
            description="Cupons que ativaram a oferta"
          />

          <progress-funnel-bar
            v-if="this.offerType === 'crosssell'"
            color="#00A186"
            is-industry
            :value="couponsTrigger"
            :percentage="couponsTriggerPercent"
            :bar-width="couponsTriggerPercent"
            description="Cupons com produto gatilho + produto com desconto"
            tooltip="Cupons que cumpriram o mínimo de ao menos um dos produtos gatilho e possuem o produto alvo."
          />

          <progress-funnel-bar
            v-if="this.offerType === 'nextbuycashback' || this.offerType === 'dynamic_rule' || this.offerType === 'crosssell'"
            color="#004D99"
            :value="couponsConsumed"
            is-industry
            :percentage="couponsConsumedPercent"
            :barWidth="couponsConsumedPercentBarWidth"
            :description="this.offerType === 'crosssell' ? 'Cupons com desconto aplicado' : 'Cupons que consumiram o benefício'"
            tooltip="Cupons que cumpriram as regras e o cliente aceitou o benefício"
          />
        </div>
      </div>

      <div class="funnel-expansion">
        <mf-button
          text
          :label="`${expandFunnel ? 'Minimizar' : 'Ver'} cupons por loja`"
          :icon="`expand_${expandFunnel ? 'less' : 'more'}`"
          text-transform="none"
          color="marca-0-l40"
          @click="expandFunnel = !expandFunnel"
        />
      </div>
    </v-card-text>
    <div v-if="expandFunnel">
      <coupons-per-unit-table :metrics="unitMetrics" :offer-type="offerType" :use-earn-metrics="useEarnMetrics" />
    </div>
  </v-card>
</template>

<script>
import { DateTime } from 'luxon'

export default {
  name: 'CouponsFunnelBar',
  components: {
    ProgressFunnelBar: () => import('./ProgressFunnelBar.vue'),
    CouponsPerUnitTable: () => import('@/modules/industria/ofertas-trade/views/components/CouponsPerUnitTable.vue'),
  },
  props: {
    loading: Boolean,
    offerType: {
      type: String,
      validator: (value) => ['takepay', 'nextbuycashback', 'dynamic_rule', 'general', 'crosssell', 'percentage'].includes(value),
    },
    startDate: {
      type: String,
      default: '',
    },
    metrics: {
      type: Object,
      default(_) {
        return {
          clients_metrics: {
            total: {
              tickets: 0,
            },
            with_sales: {
              tickets: 0,
            },
          },
          products_metrics: {
            totals: {
              total: {
                tickets: 0,
              },
            },
          },
          eligible_metrics: {
            tickets: 0,
          },
        }
      },
    },
    unitMetrics: {
      type: Array,
      default(_) {
        return []
      },
    },
  },
  data: () => ({
    expandFunnel: false,
  }),
  computed: {
    couponsWithProduct() {
      return this.metrics?.products_metrics?.totals?.total?.tickets || 0
    },

    couponsEligible() {
      return this.metrics?.eligible_metrics?.tickets || 0
    },

    couponsActivated() {
      return (this.useEarnMetrics ? this.metrics?.earn_metrics?.tickets : this.metrics?.clients_metrics?.with_sales?.tickets) || 0
    },

    couponsTrigger() {
      return this.metrics?.eligible_with_target_metrics?.tickets || 0
    },

    couponsConsumed() {
      return this.offerType === 'crosssell' ? this.metrics?.clients_metrics?.with_sales?.tickets || 0 : this.metrics?.burn_metrics?.tickets || 0
    },

    couponsEligiblePercent() {
      let couponsEligiblePercent = 0
      if (this.couponsWithProduct > 0) couponsEligiblePercent = this.formatFloat((this.couponsEligible * 100) / this.couponsWithProduct)
      return couponsEligiblePercent
    },

    couponsActivatedPercent() {
      let couponsActivatedPercent = 0
      if (this.couponsEligible > 0) couponsActivatedPercent = this.formatFloat((this.couponsActivated * 100) / this.couponsEligible)
      return couponsActivatedPercent
    },

    couponsTriggerPercent() {
      let couponsTriggerPercent = 0
      if (this.couponsEligible > 0) couponsTriggerPercent = this.formatFloat((this.couponsTrigger * 100) / this.couponsEligible)
      return couponsTriggerPercent
    },

    couponsConsumedPercent() {
      let couponsConsumedPercent = 0
      if (this.offerType === 'crosssell' && this.couponsTrigger > 0) {
        couponsConsumedPercent = this.formatFloat((this.couponsConsumed * 100) / this.couponsTrigger)
      } else if (this.couponsActivated > 0) {
        couponsConsumedPercent = this.formatFloat((this.couponsConsumed * 100) / this.couponsActivated)
      }
      return couponsConsumedPercent
    },

    couponsConsumedPercentBarWidth() {
      if (this.offerType === 'crosssell') {
        return (this.couponsTriggerPercent * this.couponsConsumedPercent) / 100
      } else {
        return (this.couponsActivatedPercent * this.couponsConsumedPercent) / 100
      }
    },

    useEarnMetrics() {
      return DateTime.fromFormat(this.startDate, 'yyyy-MM-dd') >= DateTime.fromObject({ year: 2024, month: 5, day: 1 }) && this.offerType === 'nextbuycashback'
    },
  },
  methods: {
    formatFloat(value) {
      if (Number.isInteger(value)) return value
      return parseFloat(value).toFixed(2)
    },
  },
}
</script>

<style lang="scss" scoped>
.title-and-tooltip {
  width: 100%;
  display: flex;
  justify-content: space-between;
}

.funnel-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.arrows-and-bar-container {
  display: flex;
  flex-direction: row;
  gap: 4px;

  .arrows {
    width: 1%;
    display: flex;
    flex-direction: column;
    gap: 16px;

    .arrow-line {
      position: relative;
      margin-top: -10px;
      width: 100%;
      height: 78px;
      border: 1px solid #969ea7;
      border-right: none;
      border-radius: 6px;
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }

    .arrow {
      position: absolute;
      width: 5px;
      height: 5px;
      border-left: 1px solid #969ea7;
      border-bottom: 1px solid #969ea7;
      bottom: -3px;
      right: 0;
      transform: rotate(226deg);
    }
  }

  .bars {
    width: 99%;
    display: flex;
    flex-direction: column;
    gap: 22px;
  }
}

.funnel-expansion {
  ::v-deep .v-btn__content {
    font-size: 14px !important;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: 0.01em;

    .mf-icon {
      font-size: 18px !important;
    }
  }
}
</style>
